import { Injectable } from '@angular/core';
import {
    animationFrameScheduler,
    fromEvent,
    Observable,
    of,
    Subject,
    Subscription,
} from 'rxjs';
import {
    auditTime,
    debounceTime,
    switchMap,
    takeWhile,
    tap,
} from 'rxjs/operators';

import { NAV_HEIGHT } from '../nav/nav.model';

export interface ScrollIntoViewContext {
    element: Element;
    callback: () => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getScrollParent = (element: any, includeHidden: boolean) => {
    let style = getComputedStyle(element);
    const excludeStaticParent = style.position === 'absolute';
    const overflowRegex = includeHidden
        ? /(auto|scroll|hidden)/
        : /(auto|scroll)/;

    if (style.position === 'fixed') return document.body;
    for (let parent = element; (parent = parent.parentElement); ) {
        style = getComputedStyle(parent);
        if (excludeStaticParent && style.position === 'static') {
            continue;
        }
        if (
            overflowRegex.test(
                style.overflow + style.overflowY + style.overflowX,
            )
        )
            return parent;
    }

    return document.body;
};

export function scrollIntoViewAuto(
    scrollContainerSelectorFn: () => string | Element,
): {
    subject: Subject<ScrollIntoViewContext>;
    observable: Observable<ScrollIntoViewContext>;
} {
    const subj = new Subject<ScrollIntoViewContext>();
    const obs = subj.pipe(
        switchMap((value) => {
            const rect = value.element.getBoundingClientRect();
            const scs = scrollContainerSelectorFn
                ? scrollContainerSelectorFn()
                : null;
            const scrollContainer =
                scs instanceof Element
                    ? scs
                    : scs
                    ? document.querySelector(scs)
                    : getScrollParent(value.element, true);
            // skip if already in view port
            if (
                rect.top < NAV_HEIGHT ||
                rect.top > window.innerHeight ||
                rect.bottom > window.innerHeight
            ) {
                let done: boolean;
                scrollView(value.element); // do the scroll
                return fromEvent(scrollContainer || window, 'scroll').pipe(
                    takeWhile(() => !done),
                    debounceTime(250), // wait 'till scroll is stable
                    switchMap(() => {
                        done = true;
                        return of(value);
                    }),
                );
            }
            return of(value);
        }),
        // wait for next animation frame
        auditTime(0, animationFrameScheduler),
        tap((value) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            value.callback && value.callback(); // invoke the callback
        }),
    );
    return { subject: subj, observable: obs };
}

export function scrollIntoViewInstant(el: Element, sel: Element): void {
    const elRect = el.getBoundingClientRect();
    const selRect = sel.getBoundingClientRect();

    // maybe there is a better way to detect if an element is drawn
    if (
        elRect.top + elRect.right + elRect.bottom + elRect.left === 0 &&
        selRect.top + selRect.right + selRect.bottom + selRect.left === 0
    ) {
        throw new Error('not found');
    }

    const scrollTop = Math.round(
        elRect.top +
            sel.scrollTop -
            selRect.top -
            selRect.height / 2 +
            elRect.height / 2,
    );
    sel.scrollTop = scrollTop;
    if (sel.scrollTop !== scrollTop) {
        throw new Error('not found');
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function scrollView(el: Element) {
    if (!el) {
        return;
    }
    try {
        el.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
            inline: 'center',
        });
    } catch (e) {
        el.scrollIntoView();
    }
}

@Injectable()
export class BringIntoFocusService {
    mutations: MutationObserver;
    currentQuerySelector: Subject<string> = new Subject<string>();
    qsSubscriber: Subscription;
    selector: string;
    scrollContainerSelector: () => string;
    callback: () => void;
    private readonly _next: Subject<ScrollIntoViewContext>;

    constructor() {
        this.qsSubscriber = this.currentQuerySelector.subscribe((f) => {
            this.selector = f;
        });

        this.initMutationObserver();

        // contents of the callback could cancel scrollIntoView animation
        const { subject, observable } = scrollIntoViewAuto(
            this.scrollContainerSelector,
        );
        this._next = subject;
        observable.subscribe();
    }

    registerSelector(qselector: string, callback?: () => void): void {
        this.callback = callback;
        this.currentQuerySelector.next(qselector);
    }

    registerMutatorSelector(
        qselector: string,
        scrollContainerSelector?: () => string,
    ): void {
        if (!this.mutations) {
            this.initMutationObserver();
        }

        this.scrollContainerSelector = scrollContainerSelector;
        const el = document.querySelector(qselector);

        if (el) {
            this.mutations.observe(el, {
                // attributes: true,
                childList: true,
                // characterData: true,
                subtree: true,
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    stopObserving() {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.mutations && this.mutations.disconnect();
        this.mutations = null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initMutationObserver() {
        this.mutations = new MutationObserver(() => {
            if (!this.selector) {
                return;
            }
            const el = document.querySelector(this.selector);
            if (el) {
                this._next.next({ element: el, callback: this.callback });

                this.selector = null;
                this.callback = null;
            }
        });
    }
}
